/**
 * app/components/ReceiptSelector.tsx
 * ----------------------------------
 * What: UI for selecting/uploading receipt images and attaching them to items.
 * Why: Streamlines common receipt workflows with a consistent UX.
 */
import React, { useEffect, useState } from 'react';
import { Modal, View, Text, TouchableOpacity, FlatList, ActivityIndicator, StyleSheet, TextInput, SafeAreaView } from 'react-native';
import { supabase } from '../../lib/supabase';
import { useTheme } from '../../lib/ThemeContext';
import { useSubscription } from '../../lib/SubscriptionContext';
import { Ionicons } from '@expo/vector-icons';
import ThemedButton from './ThemedButton';

export interface Receipt {
  id: string;
  file_path: string;
  place_of_purchase: string | null;
  purchase_date: string | null;
  uploaded_at?: string;
  items?: { name: string }[];
}

interface ReceiptSelectorProps {
  visible: boolean;
  filterPlace?: string | null;
  onClose: () => void;
  onSelect: (receipt: Receipt) => void;
}

const ReceiptSelector: React.FC<ReceiptSelectorProps> = ({ visible, filterPlace, onClose, onSelect }) => {
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const { theme } = useTheme();
  const { dateFormat } = useSubscription();

  useEffect(() => {
    if (visible) fetchReceipts();
  }, [visible]);

  const fetchReceipts = async () => {
    setLoading(true);
    try {
      let query = supabase.from('receipts').select('id, file_path, place_of_purchase, purchase_date, uploaded_at, items ( name )');
      if (filterPlace) {
        query = query.ilike('place_of_purchase', `%${filterPlace}%`);
      }
      const { data, error } = await query;
      if (error) throw error;
      setReceipts(data ?? []);
    } catch (e) {
      console.error('Failed to load receipts', e);
    } finally {
      setLoading(false);
    }
  };

  const filtered = receipts.filter(r =>
    !search || (r.place_of_purchase || '').toLowerCase().includes(search.toLowerCase())
  );

  const clearInitialFilter = async () => {
    // Re-fetch without place filter
    setSearch('');
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('receipts')
        .select('id, file_path, place_of_purchase, purchase_date, uploaded_at, items ( name )');
      if (error) throw error;
      setReceipts(data ?? []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    switch (dateFormat) {
      case 'DD/MM/YYYY':
        return date.toLocaleDateString('en-GB');
      case 'MM/DD/YYYY':
        return date.toLocaleDateString('en-US');
      case 'YYYY-MM-DD':
        return date.toISOString().split('T')[0];
      default:
        return date.toLocaleDateString('en-GB');
    }
  };

  const renderItem = ({ item }: { item: Receipt & { uploaded_at?: string; items?: { name: string }[] } }) => {
    const itemCount = item.items?.length || 0;
    let itemsLabel = '-';
    if (itemCount > 0) {
      const names = item.items!.map(i => i.name);
      if (itemCount <= 3) itemsLabel = names.join(', ');
      else itemsLabel = `${names.slice(0, 2).join(', ')} (+${itemCount - 2})`;
    }
    return (
      <TouchableOpacity style={[styles.item, { borderBottomColor: theme.border }]} onPress={() => onSelect(item)}>
        <Text style={[styles.lineText, { color: theme.text }]}>Place of Purchase: <Text style={{ fontWeight: '500' }}>{item.place_of_purchase || '-'}</Text></Text>
        <Text style={[styles.subText, { color: theme.secondaryText }]}>Purchase Date: {formatDate(item.purchase_date)}</Text>
        <Text style={[styles.subText, { color: theme.secondaryText }]}>Date Added: {formatDate(item.uploaded_at || null)}</Text>
        <Text style={[styles.subText, { color: theme.secondaryText }]}>Items using this: {itemsLabel}</Text>
      </TouchableOpacity>
    );
  };

  return (
    <Modal visible={visible} animationType="slide" onRequestClose={onClose}>
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
        {/* Header */}
        <View style={[styles.headerRow, { borderBottomColor: theme.border }]}>
          <TouchableOpacity onPress={onClose} accessibilityRole="button" style={styles.backBtn}>
            <Ionicons name="arrow-back" size={24} color={theme.text} />
          </TouchableOpacity>
          <Text style={[styles.title, { color: theme.text }]}>Select Receipt</Text>
          <View style={{ width: 40 }} />
        </View>
        <TextInput
          placeholder="Search place of purchase"
          placeholderTextColor={theme.secondaryText}
          style={[styles.searchInput, { color: theme.text, borderColor: theme.border, backgroundColor: theme.inputBackground }]}
          value={search}
          onChangeText={setSearch}
        />
        {/* clear filter button */}
        {filterPlace ? (
          <ThemedButton
            title="Show All"
            variant="secondary"
            onPress={clearInitialFilter}
            style={{ alignSelf: 'flex-start', marginLeft: 12, marginBottom: 6, paddingHorizontal: 12, paddingVertical: 6 }}
            textStyle={{ fontSize: 14 }}
          />
        ) : null}
        {loading ? (
          <ActivityIndicator />
        ) : (
          <FlatList data={filtered} keyExtractor={r => r.id} renderItem={renderItem} />
        )}
      </SafeAreaView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: 12,
  },
  backBtn: {
    width: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    flex: 1,
    textAlign: 'center',
    fontSize: 17,
    fontWeight: '500',
  },
  searchInput: {
    borderWidth: 1,
    borderRadius: 6,
    padding: 8,
    margin: 12,
  },
  item: {
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: 12,
  },
  lineText: {
    fontSize: 14,
    marginBottom: 2,
  },
  subText: {
    fontSize: 12,
    marginBottom: 1,
  },
});

export default ReceiptSelector; 